<template>
  <div
    :class="[
      $style.base,
      {
        [$style.editable]: editable,
      }
    ]"
    v-test="'register-transaction'"
  >
    <div :class="$style.heading">
      <div :class="$style.name">
        <BaseHeading
          v-if="type"
          size="s"
        >
          {{ $t(`global.payments.${type.toLowerCase()}`, brandNamePay()) }}
        </BaseHeading>
        <BaseText
          v-if="isGiftcardOrPrepaidCard"
          inline
        >
          {{ transaction.name }}
        </BaseText>
        <BaseText
          v-else-if="cashupEnabled"
          size="s"
        >
          {{ filters.dateTime(transaction.transactionAt || dayjs()) }}
        </BaseText>
      </div>
      <BaseText v-test="'register-transaction-amount'">
        {{ filters.currency(transaction.amount || 0) }}
      </BaseText>
      <div
        v-if="deletable && editable"
        :class="$style.deletable"
      >
        <BaseMore
          :options="['delete']"
          size="s"
          @select="
            cashupEnabled
              ? removeTransaction(props.transaction)
              : removeAllTransactions(props.transaction)
          "
          v-test="'register-transaction-options'"
        />
      </div>
      <div
        v-else-if="editable"
        :class="$style.undeletable"
        v-test="'register-transaction-disabled'"
      >
        <BaseIcon
          name="slash"
          :tooltip="{
              text: $t(
                `register.${cashupEnabled && transaction.type !== 'POS' && transaction.type !== 'ONLINE' && transaction.type !== 'GIFTY' ? 'payment_undeletable_tooltip' : 'transaction_undeletable'}`
              ),
              position: 'left',
              width: 100
            }"
          size="s"
        />
      </div>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { computed } from 'vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import type { OrderTransaction } from '@/modules/register/stores/order';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import { useRegisterStore } from '@/modules/register/stores/register';

const { order, removeTransaction, removeAllTransactions } =
  useRegisterOrderStore();
const { cashupEnabled } = useRegisterStore();

const props = defineProps<{
  editable?: boolean;
  transaction: OrderTransaction;
}>();

const type = computed(() => props.transaction.type);
const isGiftcardOrPrepaidCard = computed(
  () =>
    type.value === 'GIFTCARD' ||
    type.value === 'PREPAID_CARD' ||
    type.value === 'GIFTY',
);

const hasSavedGiftyPayment = computed(() =>
  order.transactions.some(
    (transaction) => transaction.id && transaction.type === 'GIFTY',
  ),
);

// POS, ONLINE and saved GIFTY payments can't be deleted: the money was captured outside Salonized and can only be refunded there.
// GIFTY checks the whole order, because without cashup one row combines all Gifty payments and deleting it removes all of them.
const deletable = computed(
  () =>
    type.value !== 'POS' &&
    type.value !== 'ONLINE' &&
    !(type.value === 'GIFTY' && hasSavedGiftyPayment.value) &&
    (!cashupEnabled || props.transaction.deletable),
);
</script>

<style
  lang="scss"
  module
>
.heading {
  display: flex;
  align-items: center;
  transition: background-color 1s $easeOutExpo;
}

.name {
  display: flex;
  align-items: center;
  flex: 1;
  gap: $spacing;
}

.undeletable {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 20px;
}

.deletable {
  padding: 0 $spacing * 0.5;
}
</style>
